import { useRef, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { ReactComponent as InviteIcon } from '@elementary/common-ui/assets/invite.svg';
import {
  Loading,
  Flex,
  Button,
  FlexColumn,
  Breadcrumbs,
  Dialog,
  SearchField,
  Chip,
} from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import { List, Column } from '@elementary/common-ui/components/Table';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { ACCOUNT_USERS } from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import {
  InheritedRole,
  TeamUser,
  TeamUserRole,
  UserInviteForm,
  UserUpdateForm,
} from 'shared/types';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { useRoles } from '../shared/useRoles';
import { Role } from '../shared/types';
import UserCard from '../shared/UserCard';
import InviteUserModal from './InviteUserModal';
import UpdateUserModal from './UpdateUserModal';
import UserMenu from './UserMenu';

const MAX_VISIBLE_INHERITED_ROLES = 2;

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  gap: 24px;
`;

const RolesCellWrapper = styled(Flex)`
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

const DirectRoleName = styled.span`
  color: ${({ theme }) => theme.palette.common.black};
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TooltipTrigger = styled.span`
  display: inline-flex;
  min-width: 0;
`;

const getInheritedRoleTooltip = ({ group_names }: InheritedRole) =>
  `Inherited from ${pluralizeString(
    group_names.length,
    'group',
    'groups',
  )}: ${group_names.join(', ')}`;

const getHiddenInheritedRolesTooltip = (roles: InheritedRole[]) =>
  roles
    .map((role) => `${role.name} (${getInheritedRoleTooltip(role)})`)
    .join('\n');

type UserRolesCellProps = {
  inheritedRoles?: InheritedRole[];
  roleCatalog?: Role[];
  userRoles: TeamUserRole[];
};

const UserRolesCell = ({
  inheritedRoles = [],
  roleCatalog,
  userRoles,
}: UserRolesCellProps) => {
  const firstRoleId = userRoles[0]?.id;
  const directRoleName = firstRoleId
    ? roleCatalog?.find((role) => role.id === firstRoleId)?.name ||
      userRoles[0]?.name
    : undefined;
  const visibleInheritedRoles = inheritedRoles.slice(
    0,
    MAX_VISIBLE_INHERITED_ROLES,
  );
  const hiddenInheritedRoles = inheritedRoles.slice(
    MAX_VISIBLE_INHERITED_ROLES,
  );

  if (!directRoleName && inheritedRoles.length === 0) {
    return <OverflowTooltip>-</OverflowTooltip>;
  }

  return (
    <RolesCellWrapper>
      {directRoleName && <DirectRoleName>{directRoleName}</DirectRoleName>}
      {visibleInheritedRoles.map((role) => (
        <Tooltip key={role.id} title={getInheritedRoleTooltip(role)}>
          <TooltipTrigger>
            <Chip
              label={role.name}
              variant="outlined"
              size="medium"
              disabled
              overflow="hidden"
            />
          </TooltipTrigger>
        </Tooltip>
      ))}
      {hiddenInheritedRoles.length > 0 && (
        <Tooltip title={getHiddenInheritedRolesTooltip(hiddenInheritedRoles)}>
          <TooltipTrigger>
            <Chip
              label={`+${hiddenInheritedRoles.length}`}
              variant="outlined"
              size="medium"
              disabled
            />
          </TooltipTrigger>
        </Tooltip>
      )}
    </RolesCellWrapper>
  );
};

const Users = () => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const [deletedUserId, setDeletedUserId] = useState('');
  const [deleteUserName, setDeleteUserName] = useState('');
  const [updateUserId, setUpdateUserId] = useState('');
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [updateUserModalOpen, setUpdateUserModalOpen] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const resetRef = useRef<{ reset: () => void }>();

  const { data, isLoading } = useQuery([ACCOUNT_USERS], () => api.fetchUsers());

  const { roles } = useRoles();

  const { mutate: deleteUser, isLoading: deleteUserIsLoading } = useMutation(
    (id: string) => api.deleteUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setDeleteUserModalOpen(false);
      },
      onError: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setDeleteUserModalOpen(false);
      },
    },
  );

  const {
    mutate: updateUser,
    isLoading: updateUserLoading,
    error: updateError,
  } = useMutation((form: UserUpdateForm) => api.updateUser(form), {
    onSuccess: () => {
      queryClient.invalidateQueries([ACCOUNT_USERS]);
      setUpdateUserModalOpen(false);
      openSnackbar({ message: 'User updated successfully.' });
    },
    onError: () => {
      queryClient.invalidateQueries([ACCOUNT_USERS]);
      setUpdateUserModalOpen(false);
    },
  });

  const {
    mutate: inviteUser,
    isLoading: inviteUserLoading,
    error: inviteError,
    reset: resetError,
  } = useMutation(
    ({ name, email, role_id }: UserInviteForm) =>
      api.inviteUser({ name, email, role_id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setInviteUserModalOpen(false);
        resetRef?.current?.reset?.();
        openSnackbar({ message: 'Email invitation sent successfully.' });
      },
    },
  );

  const onCreateUserModalClose = useCallback(() => {
    setInviteUserModalOpen(false);
    resetRef?.current?.reset?.();
    resetError();
  }, [resetError]);

  const columns: Array<Column<TeamUser>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'User',
        filterable: false,
        renderer: ({ id, name, email, verified }) => (
          <UserCard id={id} name={name} email={email} verified={verified} />
        ),
        compareFunc: (a, b) => {
          if (a.verified && !b.verified) {
            return 1;
          }
          return a.name.localeCompare(b.name);
        },
      },
      {
        id: 'roles',
        header: 'Role',
        filterable: false,
        showValueWhenEmpty: true,
        width: 280,
        renderer: ({ roles: user_roles, inherited_roles }) => (
          <UserRolesCell
            userRoles={user_roles}
            inheritedRoles={inherited_roles}
            roleCatalog={roles}
          />
        ),
        compareFunc: (a, b) => {
          const roleNameA =
            roles?.find((role) => role.id === a.roles[0]?.id)?.name ||
            a.roles[0]?.name ||
            '';
          const roleNameB =
            roles?.find((role) => role.id === b.roles[0]?.id)?.name ||
            b.roles[0]?.name ||
            '';
          return roleNameA.localeCompare(roleNameB);
        },
      },
      {
        id: 'lastLogin',
        header: 'Last Active',
        filterable: false,
        renderer: ({ lastLogin }) => (
          <OverflowTooltip>
            {lastLogin && localizeDate(lastLogin).format('lll')}
          </OverflowTooltip>
        ),
        compareFunc: (a, b) => dateComparer(a.lastLogin, b.lastLogin),
      },
      {
        id: 'menu',
        header: '',
        renderer: ({ id, email, verified }) => (
          <UserMenu
            {...{
              id,
              email,
              verified,
              setUpdateUserId,
              setUpdateUserModalOpen,
              setDeletedUserId,
              setDeleteUserName,
              setDeleteUserModalOpen,
            }}
          />
        ),
        filterable: false,
        sortable: false,
        metaColumn: true,
      },
    ],
    [roles],
  );

  const filteredData = useMemo(
    () =>
      search.trim()
        ? data?.filter(
            (item) => item.email.includes(search) || item.name.includes(search),
          )
        : data,
    [data, search],
  );

  return isLoading ? (
    <Loading />
  ) : (
    <Wrapper>
      <FlexColumn sx={{ gap: 3 }}>
        <Breadcrumbs items={['Account', 'Users']} showTreeButton={false} />
        <Flex sx={{ gap: 1, justifyContent: 'space-between' }}>
          <SearchField
            sx={{ width: '248px' }}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <Button
            sx={{ minWidth: '142px' }}
            onClick={() => setInviteUserModalOpen(true)}
            startIcon={<InviteIcon />}
          >
            Invite user
          </Button>
        </Flex>
      </FlexColumn>

      <List columns={columns} data={filteredData} defaultSortKey="name" />

      <Dialog
        open={deleteUserModalOpen}
        onClose={() => setDeleteUserModalOpen(false)}
        onConfirmButtonClick={() => deleteUser(deletedUserId)}
        isLoading={deleteUserIsLoading}
        titleIcon={<DeleteIcon />}
        title="Delete user"
        confirmText="Delete user"
        message={`Are you sure you want to delete ${deleteUserName}?`}
        secondaryMessage="This action cannot be undone."
      />

      <UpdateUserModal
        open={updateUserModalOpen}
        onClose={() => setUpdateUserModalOpen(false)}
        onConfirmButtonClick={(form: UserUpdateForm) => updateUser(form)}
        confirmText="Update user"
        errorMessage={(updateError as any)?.response?.data?.message}
        updateUserLoading={updateUserLoading}
        user={data?.find(
          (team_user: TeamUser) => team_user.id === updateUserId,
        )}
        roles={roles}
      />

      <InviteUserModal
        ref={resetRef}
        open={inviteUserModalOpen}
        onClose={onCreateUserModalClose}
        onConfirmButtonClick={({ name, email, role_id }: UserInviteForm) =>
          inviteUser({ name, email, role_id })
        }
        confirmText="Send invitation"
        errorMessage={(inviteError as any)?.response?.data?.message}
        inviteUserLoading={inviteUserLoading}
        roles={roles}
      />
    </Wrapper>
  );
};

export default Users;
